import { useCallback, useMemo, useState } from "react";

import {
  useCancelScheduledNotification,
  useExpirePendingNotification,
} from "@/api/mutations/notifications";
import { useGetScheduledNotifications } from "@/api/queries/notifications";
import { ScrollView, Separator, Text, YStack } from "@/components";
import SettingsListItem from "./SettingsListItem";

const formatNotificationDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${dateStr} @ ${timeStr}`;
};

export default function ScheduledNotifications() {
  const { data: scheduledNotificationsData } = useGetScheduledNotifications();
  const { mutate: cancelNotification } = useCancelScheduledNotification();
  const { mutate: expireNotification } = useExpirePendingNotification();

  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  const handleCancelNotification = useCallback(
    (notificationId: string) => {
      setCancellingIds((prev) => new Set(prev).add(notificationId));
      cancelNotification(
        { notificationId },
        {
          onSettled: () => {
            setCancellingIds((prev) => {
              const next = new Set(prev);
              next.delete(notificationId);
              return next;
            });
          },
        },
      );
    },
    [cancelNotification],
  );

  const handleExpirePendingNotification = (pendingNotificationId: string) => {
    expireNotification({ pendingNotificationId });
  };

  const formattedScheduledNotifications = useMemo(() => {
    if (!scheduledNotificationsData?.scheduled_notifications) {
      return [];
    }

    return scheduledNotificationsData.scheduled_notifications.map(
      (notification) => {
        const formattedDateTime = formatNotificationDateTime(notification.date);
        const isCancelling = cancellingIds.has(notification.notification_id);

        return {
          notification_id: notification.notification_id,
          formattedDateTime,
          content: notification.content,
          isCancelling,
        };
      },
    );
  }, [scheduledNotificationsData, cancellingIds]);

  const formattedPendingNotifications = useMemo(() => {
    if (
      !scheduledNotificationsData?.pending_notifications ||
      scheduledNotificationsData.pending_notifications.length === 0
    ) {
      return [];
    }

    const now = new Date();
    return scheduledNotificationsData.pending_notifications.map(
      (notification) => {
        const formattedDateTime = formatNotificationDateTime(notification.date);
        const alreadyExpired = new Date(notification.date) <= now;

        return {
          pending_notification_id: notification.pending_notification_id,
          formattedDateTime,
          content: notification.content,
          status: notification.status,
          alreadyExpired,
        };
      },
    );
  }, [scheduledNotificationsData]);

  const hasScheduledNotifications = formattedScheduledNotifications.length > 0;
  const hasPendingNotifications = formattedPendingNotifications.length > 0;

  if (!hasScheduledNotifications && !hasPendingNotifications) {
    return null;
  }

  return (
    <ScrollView>
      <YStack gap="$150">
        {formattedScheduledNotifications.map((notification) => (
          <SettingsListItem
            key={notification.notification_id}
            title={notification.content}
            subtitle={notification.formattedDateTime}
            onDelete={() =>
              handleCancelNotification(notification.notification_id)
            }
            isDeleting={notification.isCancelling}
          />
        ))}
        {hasPendingNotifications && (
          <>
            <Separator borderColor="$fillNeutral" />
            <Text size="$sm" semibold>
              Pending Notifications
            </Text>
            {formattedPendingNotifications.map((notification) => (
              <SettingsListItem
                key={notification.pending_notification_id}
                title={notification.content}
                subtitle={`${notification.formattedDateTime} • ${notification.status}`}
                onDelete={() =>
                  handleExpirePendingNotification(
                    notification.pending_notification_id,
                  )
                }
                disabled={notification.alreadyExpired}
              />
            ))}
          </>
        )}
      </YStack>
    </ScrollView>
  );
}
